import React, { useState } from 'react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Modal from './Modal';
import { num, apiError } from '../utils/format';
import { downloadCSV, rowsFromCSV } from '../utils/csv';

// ── Stock in and out of a spreadsheet ───────────────────────────────────────
//
// A stock take happens on paper in the store and gets typed up afterwards, so
// the sheet has to leave with every line already on it — zeros included, since
// "we have none of these" is exactly what a count records — and come back with
// one column filled in.
//
// What comes back is a COUNT, not a correction: the difference against what the
// system holds is posted as one ledger movement per line. Nothing is rewritten,
// so the ledger still explains how the stock got where it is.

const HEADER_MAP = {
  locationid: 'locationId', location: 'location', branch: 'location', store: 'location',
  itemtype: 'itemType', type: 'itemType',
  itemid: 'itemId', productid: 'itemId', id: 'itemId',
  code: 'code', productcode: 'code',
  item: 'item', product: 'item', productname: 'item', name: 'item',
  variantid: 'variantId', pack: 'pack', variant: 'pack', packsize: 'pack', weightlabel: 'pack',
  onhand: 'onHand', available: 'onHand', system: 'onHand', current: 'onHand',
  counted: 'counted', count: 'counted', qty: 'counted', quantity: 'counted', newqty: 'counted',
};

export const exportStockCSV = (rows, location) => {
  const cols = [
    ['LocationId', () => location.id],
    ['Location', () => location.name],
    ['ItemType', () => 'product'],
    ['ItemId', 'productId'],
    ['Code', 'code'],
    ['Item', 'product'],
    ['VariantId', (r) => r.variantId || ''],
    ['Pack', (r) => (r.variant === '—' ? '' : r.variant)],
    ['Unit', 'unit'],
    ['On hand', (r) => num(r.qty, 3)],
    // Left blank on purpose: this is the one column anyone should type in, and
    // a blank means "not counted" rather than "counted zero".
    ['Counted', () => ''],
  ];
  const stamp = new Date().toISOString().slice(0, 10);
  downloadCSV(`stock-${String(location.name || 'branch').replace(/\W+/g, '-').toLowerCase()}-${stamp}.csv`, cols, rows);
};

export const StockImportModal = ({ location, onClose, onDone }) => {
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState('');
  const [txnType, setTxnType] = useState('adjustment');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name); setPreview(null); setError('');
    const reader = new FileReader();
    reader.onload = () => {
      const { rows: parsed } = rowsFromCSV(String(reader.result), HEADER_MAP);
      // A line nobody counted is a line nobody touched — dropping the blanks is
      // what lets someone count one shelf and upload the same sheet.
      const counted = parsed.filter((r) => String(r.counted ?? '').trim() !== '');
      setRows(counted);
      if (!counted.length) setError('No rows have a Counted quantity — fill that column in and upload again.');
    };
    reader.readAsText(file);
  };

  const send = async (dryRun) => {
    setBusy(true); setError('');
    try {
      const { data } = await api.post('/inventory/import', {
        rows: rows.map((r) => ({ ...r, locationId: r.locationId || location?.id })),
        txnType, dryRun,
      });
      if (dryRun) { setPreview(data); if (data.errors?.length) setError(`${data.errors.length} row(s) could not be matched.`); }
      else { toast.success(data.message); onDone(); onClose(); }
    } catch (e) {
      const res = e.response?.data;
      setError(apiError(e));
      if (res?.errors?.length) setPreview({ errors: res.errors, plan: [] });
    } finally { setBusy(false); }
  };

  const changes = preview?.plan?.filter((p) => p.delta !== 0) || [];
  const clean = preview && !(preview.errors || []).length;

  return (
    <Modal title="Upload counted stock" onClose={onClose} wide
      footer={<>
        <button className="btn btn-outline" onClick={onClose}>Close</button>
        <button className="btn btn-outline" onClick={() => send(true)} disabled={busy || !rows.length}>
          {busy ? 'Checking…' : 'Check first'}
        </button>
        <button className="btn btn-primary" onClick={() => send(false)} disabled={busy || !rows.length || !clean}>
          {busy ? 'Applying…' : `Apply${changes.length ? ` ${changes.length} change${changes.length === 1 ? '' : 's'}` : ''}`}
        </button>
      </>}>
      <p className="muted small" style={{ marginTop: 0 }}>
        Download the stock sheet, fill in the <strong>Counted</strong> column, and upload it here.
        Rows left blank are ignored. Each counted line is compared with what the system holds and the
        <strong> difference</strong> is posted as a stock movement — nothing is overwritten, so the ledger still
        explains itself afterwards.
      </p>

      <div className="row">
        <div className="field"><label>CSV file</label><input type="file" accept=".csv,text/csv" onChange={onFile} /></div>
        <div className="field"><label>Post as</label>
          <select value={txnType} onChange={(e) => setTxnType(e.target.value)}>
            <option value="adjustment">Stock count — an adjustment</option>
            <option value="opening">Opening stock — starting balances</option>
          </select>
        </div>
      </div>
      {fileName && <p className="muted small">{fileName} · {rows.length} counted row{rows.length === 1 ? '' : 's'}</p>}

      {preview?.errors?.length > 0 && (
        <div className="card card-pad" style={{ marginBottom: '.6rem' }}>
          <strong className="neg">These rows could not be matched — nothing will be applied until they are fixed:</strong>
          <ul className="muted small" style={{ margin: '.4rem 0 0', paddingLeft: '1.1rem' }}>
            {preview.errors.slice(0, 12).map((e, i) => <li key={i}>Line {e.line}: {e.message}</li>)}
            {preview.errors.length > 12 && <li>…and {preview.errors.length - 12} more</li>}
          </ul>
        </div>
      )}

      {preview && clean && (
        <>
          <p className="muted small">
            {changes.length} row{changes.length === 1 ? '' : 's'} would change
            {preview.summary ? ` · ${preview.summary.unchanged} already match` : ''}.
          </p>
          {changes.length > 0 && (
            <div className="table-wrap" style={{ maxHeight: 320, overflowY: 'auto' }}>
              <table className="grid">
                <thead><tr><th>Item</th><th>Pack</th><th className="right">System</th><th className="right">Counted</th><th className="right">Movement</th></tr></thead>
                <tbody>
                  {changes.slice(0, 200).map((c, i) => (
                    <tr key={i}>
                      <td>{c.item}</td>
                      <td className="muted">{c.pack || '—'}</td>
                      <td className="right muted">{num(c.current, 3)}</td>
                      <td className="right">{num(c.counted, 3)}</td>
                      <td className={`right ${c.delta > 0 ? 'pos' : 'neg'}`}>{c.delta > 0 ? '+' : ''}{num(c.delta, 3)}</td>
                    </tr>
                  ))}
                  {changes.length > 200 && <tr><td colSpan={5} className="muted">…and {changes.length - 200} more</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {error && <p className="form-error">{error}</p>}
    </Modal>
  );
};
